import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import { TEAPOT_CHEF } from 'Utils/constant';
import { useAuth } from 'Hooks/useAuth';
import { useNotification } from 'Hooks/useNotification';
import { useResponsive } from 'Hooks/useResponsive';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'Utils/routes';
import { userService } from 'Services/userService';
import { UserProfile } from 'Types/api';
import './ButtonAppBar.scss';

const pages = [
  { label: 'Home', route: ROUTES.HOME },
  { label: 'Dashboard', route: ROUTES.DASHBOARD },
];
const settings = ['Account', 'Logout'];

const ButtonAppBar = () => {
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [profile, setProfile] = React.useState<UserProfile | null>(null);
  const { isAuthenticated, email, signOut } = useAuth();
  const { showNotification } = useNotification();
  const { isDesktop } = useResponsive();
  const navigate = useNavigate();

  // Fetch user profile when authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      userService
        .getProfile()
        .then(setProfile)
        .catch(() => void 0);
    } else {
      setProfile(null);
    }
  }, [isAuthenticated]);

  // Auto-close mobile nav menu when switching to desktop view
  React.useEffect(() => {
    if (isDesktop && anchorElNav) {
      setAnchorElNav(null);
    }
  }, [isDesktop, anchorElNav]);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleNavMenuItemClick = (route: string) => {
    setAnchorElNav(null);
    navigate(route);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleMenuItemClick = async (event: React.MouseEvent<HTMLElement>, index: number) => {
    setAnchorElUser(null);
    if (index === 0) {
      // Account - navigate to profile
      navigate(ROUTES.PROFILE);
    } else if (index === 1) {
      // Logout
      const result = await signOut();
      if (result.success) {
        navigate(ROUTES.HOME);
      } else {
        showNotification(result.message);
      }
    }
  };

  const getAvatarInitial = () => {
    if (profile?.displayName) {
      return profile.displayName[0].toUpperCase();
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return '?';
  };

  return (
    <AppBar position='static' color='transparent' elevation={0} component='header'>
      <Container maxWidth='xl'>
        <Toolbar disableGutters component='nav' aria-label='Main navigation'>
          {/* desktop view */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, mr: 1 }}>
            <img src='/teapotchef-logo.svg' alt='Logo' className='logo' />
          </Box>

          <Typography
            variant='h6'
            noWrap
            component='a'
            href={ROUTES.HOME}
            color='primary'
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' },
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.1rem',
              textDecoration: 'none',
            }}
          >
            {TEAPOT_CHEF}
          </Typography>

          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {pages.map((page) => (
              <Button
                key={page.label}
                onClick={() => handleNavMenuItemClick(page.route)}
                sx={{ my: 2, color: 'inherit', display: 'block' }}
              >
                {page.label}
              </Button>
            ))}
          </Box>

          {/* mobile view  */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size='large'
              aria-label='Open navigation menu'
              aria-controls='nav-menu-mobile'
              aria-haspopup='true'
              aria-expanded={Boolean(anchorElNav)}
              onClick={handleOpenNavMenu}
              color='inherit'
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id='nav-menu-mobile'
              anchorEl={anchorElNav}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              sx={{
                display: { xs: 'block', md: 'none' },
              }}
            >
              {pages.map((page) => (
                <MenuItem key={page.label} onClick={() => handleNavMenuItemClick(page.route)}>
                  <Typography textAlign='center'>{page.label}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <Box sx={{ mr: 1, display: { xs: 'flex', md: 'none' } }}>
            <img src='/teapotchef-logo.svg' alt='Logo' width={32} className='logo' />
          </Box>

          <Typography
            variant='h6'
            noWrap
            component='a'
            href={ROUTES.HOME}
            color='primary'
            sx={{
              display: { xs: 'flex', md: 'none' },
              flexGrow: 1,
              fontFamily: 'monospace',
              fontWeight: 700,
              letterSpacing: '.1rem',
              textDecoration: 'none',
            }}
          >
            {TEAPOT_CHEF}
          </Typography>

          <Box sx={{ flexGrow: 0 }}>
            {isAuthenticated ? (
              <Box>
                <Tooltip title={profile?.displayName || email || 'Account settings'}>
                  <IconButton
                    onClick={handleOpenUserMenu}
                    sx={{ p: 0 }}
                    aria-label='Open account menu'
                    aria-controls='user-menu'
                    aria-haspopup='true'
                    aria-expanded={Boolean(anchorElUser)}
                  >
                    <Avatar src={profile?.avatarUrl}>{getAvatarInitial()}</Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: '45px' }}
                  id='user-menu'
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {settings.map((setting, index) => (
                    <MenuItem key={setting} onClick={(event) => handleMenuItemClick(event, index)}>
                      <Typography textAlign='center'>{setting}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            ) : (
              <Button
                color='inherit'
                onClick={() => {
                  navigate(ROUTES.SIGN_IN);
                }}
              >
                Login
              </Button>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default ButtonAppBar;
